import type { RoomDep, RoomActionsContainer } from "../room.types";
import type { RoomStateInternal } from "../roomState/roomState";
import type { Mate } from "@merels/room/types";
import type { SetSavedGameOptionsArgs, RestartGameArgs, GameSavedOptions, ChallengeConfiguration } from "@merels/room";
import { hashGameState } from "@merels/game-logic";

export function makeRoomActions(
  container: RoomActionsContainer,
  state: RoomStateInternal,
  config: { clientId: string }
): RoomDep["actions"] {
  const { clientId } = config;
  const { moveHistory } = container

  const updateLocalMates = (newMates: Mate[]) => {
    state.pendingLocalMates = newMates;
    
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (proxy && state.status.value === "connected") {
      proxy.updateMates({ clientId, mates: newMates }).catch((e: any) => {
        console.error("Failed to update local mates:", e);
        state.error.value = "Failed to update mates";
      });
    } else {
      //console.log('[RoomActions] Not connected or no proxy, only updating pendingLocalMates')
    }
  };

  const startGame = async (gameId: string, options?: any) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");

    // New game, new history.
    moveHistory.clear()
    await proxy.startGame({
      gameId,
      ...(options || {}),
    });
  };

  const endGame = async () => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");

    moveHistory.clear()
    await proxy.endGame();
  };

  const submitMove = (move: any, mateId: string) => recordMove(move, mateId, false);
  const submitHostMove = (move: any, mateId: string) => recordMove(move, mateId, true);

  const recordMove = async (move: any, mateId: string, asHost: boolean) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");

    // Record move immediately so the UI can show a pending entry (⏳).
    moveHistory.addEntry({
      move,
      mateID: mateId,
      success: false,
    })

    // Hash of the state this client believes precedes the move, so other
    // clients can detect replay divergence when they apply it (see
    // roomConnection.ts's reconstruct()).
    const stateHash = hashGameState({
      G: state.activeGame.value?.gameState,
      ctx: state.activeGame.value?.ctx,
    })

    try {
      const result = await proxy.makeMove({
        move,
        mateId,
        stateHash,
        ...(asHost ? { asHost: true } : {}),
      });

      moveHistory.updateLastEntry({
        success: result.success,
        error: result.success ? undefined : (result.error || 'Move failed'),
        response: result,
      })

      if (!result.success) {
        throw new Error(result.error || 'Move failed')
      }
    } catch (e) {
      moveHistory.updateLastEntry({
        success: false,
        error: (e as Error)?.message || String(e),
      })
      throw e
    }
  };

  const forceGameState = async (newState: { G?: unknown; ctx?: unknown }, basedOnSeqIndex?: number) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value
    if (!proxy) throw new Error('Not connected to a room')

    // State injection typically invalidates move semantics.
    moveHistory.clear()
    const result = await proxy.forceGameState({ newState, basedOnSeqIndex })
    if (!result.success) throw new Error(result.error || 'Failed to force game state')
    return result
  }

  const flushMates = async () => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");

    moveHistory.clear()
    await proxy.flushMates();
  };

  const openGame = async (gameId: string, options?: any) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    moveHistory.clear()
    const result = await proxy.openGame({ gameId, clientId, ...options });
    if (result && typeof result === 'object' && 'success' in result && !result.success) {
      const errorMsg = (result as any).error || 'Failed to open game'
      console.error('[RoomActions] openGame failed:', errorMsg)
      throw new Error(errorMsg)
    }
    return result as { outcome?: 'gameStarted' | 'setupStarted'; setup?: { slotCount?: number | null } } | void;
  };

  const choosePlayerCount = async (count: number) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.choosePlayerCount({ count });
  };

  const updateMateAssignment = async (mateId: string, slotIndex: number | null) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.updateMateAssignment({ mateId, slotIndex, clientId });
  };

  const startGameFromSetup = async () => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.startGameFromSetup();
  };

  const cancelGameSetup = async () => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.cancelGameSetup();
  };

  const getSavedGameOptions = async (gameId: string): Promise<GameSavedOptions> => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    return await proxy.getSavedGameOptions({ gameId });
  };

  const getThumbnailHistory = async (persistentId: string): Promise<string[]> => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const { history } = await proxy.getThumbnailHistory({ persistentId });
    return history;
  };

  const deleteThumbnailFromHistory = async (persistentId: string, url: string): Promise<string[]> => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const { history } = await proxy.deleteThumbnailFromHistory({ persistentId, url });
    return history;
  };

  const setSavedGameOptions = async (args: SetSavedGameOptionsArgs) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.setSavedGameOptions(args);
  };

  const restartGame = async (args?: RestartGameArgs) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    moveHistory.clear()
    await proxy.restartGame(args || {});
  };

  const getGameChannelPreference = async (gameId: string): Promise<string | null> => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const { channel } = await proxy.getGameChannelPreference({ gameId });
    return channel;
  };

  const setGameChannelPreference = async (gameId: string, channel: string) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.setGameChannelPreference({ gameId, channel });
  };

  const getChallengeConfigurationPreference = async (gameId: string): Promise<ChallengeConfiguration | null> => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    return (await proxy.getChallengeConfigurationPreference({ gameId })).challengeConfiguration;
  };

  const setChallengeConfigurationPreference = async (gameId: string, challengeConfiguration: ChallengeConfiguration) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.setChallengeConfigurationPreference({ gameId, challengeConfiguration });
  };

  const setGamelessMode = async (gameless: boolean) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.setGamelessMode({ gameless });
  };

  const setHost = async (mateId: string, isHost: boolean) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const result = await proxy.setHost({ mateId, isHost });
    if (!result.success) throw new Error(result.error || 'Failed to change host');
  };

  const setManagedMode = async (managed: boolean) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const result = await proxy.setManagedMode({ managed });
    if (!result.success) throw new Error(result.error || 'Failed to change managed mode');
  };

  const setHostTerm = async (hostTerm: string) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const result = await proxy.setHostTerm({ hostTerm });
    if (!result.success) throw new Error(result.error || 'Failed to rename hosts');
  };

  const setCuratedMode = async (curated: boolean) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const result = await proxy.setCuratedMode({ curated });
    if (!result.success) throw new Error(result.error || 'Failed to change curation');
  };

  const setCuratedGames = async (gameIds: string[]) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    const result = await proxy.setCuratedGames({ gameIds });
    if (!result.success) throw new Error(result.error || 'Failed to change curated games');
  };

  const setActiveGameVersion = async (
    gameId: string,
    version: import('@merels/room').ResolvedGameVersion
  ) => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy) throw new Error("Not connected to a room");
    await proxy.setActiveGameVersion({ gameId, version });
  };

  return {
    updateLocalMates,
    startGame,
    endGame,
    submitMove,
    submitHostMove,
    forceGameState,
    flushMates,
    openGame,
    choosePlayerCount,
    updateMateAssignment,
    startGameFromSetup,
    cancelGameSetup,
    getSavedGameOptions,
    getThumbnailHistory,
    deleteThumbnailFromHistory,
    setSavedGameOptions,
    restartGame,
    getGameChannelPreference,
    setGameChannelPreference,
    getChallengeConfigurationPreference,
    setChallengeConfigurationPreference,
    setGamelessMode,
    setHost,
    setManagedMode,
    setHostTerm,
    setCuratedMode,
    setCuratedGames,
    setActiveGameVersion,
  };
}
